import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const PetContext = createContext();
export const usePets = () => useContext(PetContext);

export const PetProvider = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [pets, setPets] = useState([]);
    const [assessments, setAssessments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasLoadedPets, setHasLoadedPets] = useState(false);

    const fetchPets = useCallback(async (options = {}) => {
        if (!isAuthenticated) return;
        const { force = false } = options;
        if (hasLoadedPets && !force) return;
        
        setLoading(true);
        try {
            const response = await axios.get('/pets/');
            setPets(response.data);
            setHasLoadedPets(true);
            setError(null);
        } catch (err) {
            console.error('Error fetching pets:', err);
            setError('Failed to load pets');
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, hasLoadedPets]);

    const fetchAssessments = useCallback(async () => {
        if (!isAuthenticated) return;
        
        try {
            const response = await axios.get('/assessments/');
            setAssessments(response.data);
        } catch (err) {
            console.error('Error fetching assessments:', err);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated && !hasLoadedPets && !loading) {
            fetchPets();
        }
        if (!isAuthenticated) {
            setPets([]);
            setAssessments([]);
            setHasLoadedPets(false);
        }
    }, [isAuthenticated, hasLoadedPets, loading, fetchPets]);

    const addPet = async (petData) => {
        try {
            const response = await axios.post('/pets/', petData);
            setPets(prev => [...prev, response.data]);
            return { success: true, data: response.data };
        } catch (err) {
            console.error('Error adding pet:', err);
            return { success: false, error: err.response?.data };
        }
    };

    const createAssessment = async (assessmentData) => {
        try {
            const response = await axios.post('/assessments/create_assessment/', assessmentData);
            setAssessments(prev => [response.data, ...prev]);
            return { success: true, data: response.data };
        } catch (err) {
            console.error('Error creating assessment:', err);
            return { success: false, error: err.response?.data };
        }
    };

    const value = {
        pets,
        assessments,
        loading,
        error,
        fetchPets,
        fetchAssessments,
        addPet,
        createAssessment
    };

    return <PetContext.Provider value={value}>{children}</PetContext.Provider>;
};

export default PetContext;
